/**
 * PriorityListCard.tsx
 *
 * Renders the `priority_list` envelope data emitted by onething_get_user_priorities
 * (and team_priorities once migrated). Reuses existing token maps from
 * priorityCardTokens.ts — no new colors.
 */

import { Target, Inbox, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getPriorityStatusMeta, priorityTypeBadgeClass, priorityTypeAccentClass } from "@/components/priorities/priorityCardTokens";
import { PriorityStatusTooltip } from "@/components/priorities/PriorityStatusTooltip";
import { PriorityDetailDialog } from "@/components/priorities/PriorityDetailDialog";
import { priorityService } from "@/services/priorityService";
import { PriorityStatus, PriorityType } from "@/types";
import { useLazyEntityDetail } from "./useLazyEntityDetail";

// ---------------------------------------------------------------------------
// Data shape emitted by the BE envelope for kind "priority_list"
// ---------------------------------------------------------------------------

export interface PriorityListRow {
  pid: string;
  code: string;
  name: string;
  type: PriorityType;
  status: PriorityStatus;
  driName?: string;
  pppNames?: string[];
  /** No DRI yet — sits in the candidate pool under its PPP */
  isCandidate?: boolean;
}

// ---------------------------------------------------------------------------
// Single row
// ---------------------------------------------------------------------------

function PriorityRow({
  row,
  isLoading,
  onActivate,
}: {
  row: PriorityListRow;
  isLoading: boolean;
  onActivate: (pid: string) => void;
}) {
  const statusMeta = getPriorityStatusMeta(row.status);
  const StatusIcon = statusMeta.icon;

  return (
    <div
      className="flex items-start gap-2.5 mx-1 rounded-lg px-2 py-2.5 transition-colors hover:bg-muted/50 cursor-pointer"
      role="button"
      tabIndex={0}
      aria-label={`Open priority ${row.code} ${row.name}`}
      onClick={() => onActivate(row.pid)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onActivate(row.pid);
        }
      }}
    >
      {/* Type accent bar — full row-height pill */}
      <div className={`w-[3px] self-stretch min-h-[34px] rounded-full flex-shrink-0 ${priorityTypeAccentClass[row.type]}`} />

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-mono text-[10.5px] font-semibold text-muted-foreground">{row.code}</span>
          <span className="text-[13px] font-semibold tracking-tight truncate">{row.name}</span>
          {row.isCandidate && (
            <span className="rounded border border-dashed border-border px-1 text-[9px] font-semibold uppercase text-muted-foreground">Candidate</span>
          )}
        </div>
        {(row.driName || row.pppNames?.length) && (
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {[row.driName, row.pppNames?.join(", ")].filter(Boolean).join(" · ")}
          </p>
        )}
      </div>

      {/* Badges */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {isLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
        <Badge
          variant="outline"
          className={`rounded-full text-[10.5px] font-semibold px-2 py-0.5 border ${priorityTypeBadgeClass[row.type]}`}
        >
          {row.type}
        </Badge>
        <PriorityStatusTooltip status={row.status}>
          <Badge
            variant="outline"
            className={`gap-1 rounded-full text-[10.5px] font-semibold px-2 py-0.5 border ${statusMeta.className}`}
          >
            <StatusIcon className="h-2.5 w-2.5" />
            {statusMeta.label}
          </Badge>
        </PriorityStatusTooltip>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface PriorityListCardProps {
  summary: string;
  rows: PriorityListRow[];
}

export function PriorityListCard({ summary, rows }: PriorityListCardProps) {
  const { entity, isLoading, openPid, selectedPid, close } = useLazyEntityDetail(
    "priority",
    priorityService.getPriority,
  );

  return (
    <Card className="my-2 rounded-xl border-border/60 shadow-sm dark:shadow-none dark:ring-1 dark:ring-border/40 overflow-hidden">
      <CardHeader className="flex flex-row items-start gap-2.5 px-4 py-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Target className="h-3.5 w-3.5" />
        </div>
        <p className="text-[13.5px] font-bold leading-tight tracking-tight flex-1 min-w-0">
          {summary}
        </p>
        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
          {rows.length}
        </span>
      </CardHeader>
      <CardContent className="px-2 pb-2 pt-0">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-muted-foreground">
            <Inbox className="h-6 w-6 opacity-50" />
            <span className="text-xs">No active priorities.</span>
          </div>
        ) : (
          <div>
            {rows.map((row) => (
              <PriorityRow
                key={row.pid}
                row={row}
                isLoading={isLoading && selectedPid === row.pid}
                onActivate={openPid}
              />
            ))}
          </div>
        )}
      </CardContent>
      {/* Only mount the dialog once the full entity has loaded — avoids a
          flash of the "create" form (priority=null) while fetching. */}
      <PriorityDetailDialog
        open={!!selectedPid && !!entity}
        onOpenChange={(open) => !open && close()}
        priority={entity ?? null}
      />
    </Card>
  );
}
